// src/fabric/conversation.js

import { RealtimeUtils } from '@theodoreniu/realtime-api-beta';

export class FabricConversation {
  constructor() {
    this.defaultFrequency = 24000; // 24kHz
    this.clear();
    this.EventProcessors = this.createEventProcessors();
  }

  clear() {
    this.itemLookup = {};
    this.items = [];
    this.responseLookup = {};
    this.responses = [];
    this.queuedSpeechItems = {};
    this.queuedTranscriptItems = {};
    this.queuedInputAudio = null;
    return true;
  }

  createEventProcessors() {
    return {
      'fabric.conversation.item.created': (event) => {
        const { item } = event;
        const newItem = JSON.parse(JSON.stringify(item));
        
        if (!this.itemLookup[newItem.id]) {
            this.itemLookup[newItem.id] = newItem;
            this.items.push(newItem);
          }
  
          newItem.formatted = {
            audio: new Int16Array(0),
            text: '',
            transcript: ''
          };
  
          // Handle queued speech items
          if (this.queuedSpeechItems[newItem.id]) {
            newItem.formatted.audio = this.queuedSpeechItems[newItem.id].audio;
            delete this.queuedSpeechItems[newItem.id];
          }
  
          // Handle text content
          if (newItem.content) {
            const textContent = newItem.content.filter(c => 
              ['text', 'input_text'].includes(c.type)
            );
            for (const content of textContent) {
              newItem.formatted.text += content.text;
            }
          }
  
          // Handle queued transcripts
          if (this.queuedTranscriptItems[newItem.id]) {
            newItem.formatted.transcript = this.queuedTranscriptItems[newItem.id].transcript;
            delete this.queuedTranscriptItems[newItem.id];
          }
  
          // Set status based on item type
          if (newItem.type === 'message') {
            if (newItem.role === 'user') {
              newItem.status = 'completed';
              if (this.queuedInputAudio) {
                newItem.formatted.audio = this.queuedInputAudio;
                this.queuedInputAudio = null;
              }
            } else {
              newItem.status = 'in_progress';
            }
          } else if (newItem.type === 'function_call') {
            newItem.formatted.tool = {
              type: 'function',
              name: newItem.name,
              call_id: newItem.call_id,
              arguments: ''
            };
            newItem.status = 'in_progress';
          } else if (newItem.type === 'function_call_output') {
            newItem.status = 'completed';
            newItem.formatted.output = newItem.output;
          }
  
          return { item: newItem, delta: null };
        },
  
        'fabric.conversation.item.deleted': (event) => {
          const { item_id } = event;
          const item = this.itemLookup[item_id];
          if (!item) {
            throw new Error(`item.deleted: Item "${item_id}" not found`);
          }
          delete this.itemLookup[item.id];
          const index = this.items.indexOf(item);
          if (index > -1) {
            this.items.splice(index, 1);
          }
          return { item, delta: null };
        },
  
        'fabric.response.created': (event) => {
          const { response } = event;
          if (!this.responseLookup[response.id]) {
            this.responseLookup[response.id] = response;
            this.responses.push(response);
          }
          return { item: null, delta: null };
        },
  
        'fabric.response.output_item.added': (event) => {
          const { response_id, item } = event;
          const response = this.responseLookup[response_id];
          if (!response) {
            throw new Error(`response.output_item.added: Response "${response_id}" not found`);
        }
        response.output.push(item.id);
        return { item: null, delta: null };
      },

      'fabric.response.output_item.done': (event) => {
        const { item } = event;
        if (!item) {
          throw new Error('response.output_item.done: Missing "item"');
        }
        const foundItem = this.itemLookup[item.id];
        if (!foundItem) {
          throw new Error(`response.output_item.done: Item "${item.id}" not found`);
        }
        foundItem.status = item.status;
        return { item: foundItem, delta: null };
      },

      'fabric.response.done': (event) => {
        const { response_id } = event;
        const response = this.responseLookup[response_id];
        if (!response) {
          throw new Error(`response.done: Response "${response_id}" not found`);
        }
        response.status = 'completed';
        return { item: null, delta: null };
      }
    };
  }

  processEvent(event, ...args) {
    if (!event.event_id) {
      console.error(event);
      throw new Error('Missing "event_id" on event');
    }
    if (!event.type) {
      console.error(event);
      throw new Error('Missing "type" on event');
    }
    const eventProcessor = this.EventProcessors[event.type];
    if (!eventProcessor) {
      throw new Error(`Missing conversation event processor for "${event.type}"`);
    }
    return eventProcessor.call(this, event, ...args);
  }

  getItem(id) {
    return this.itemLookup[id] || null;
  }

  getItems() {
    return this.items.slice();
  }

  queueInputAudio(inputAudio) {
    this.queuedInputAudio = inputAudio;
    return inputAudio;
  }
}